import { useState, useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import jxLogger from '../../../services/utils/jxLogger.js';
import umlCanvasEngine from '../../Core/UmlCanvasEngine.js';
import umlConnectorEngine from '../../Core/UmlConnectorEngine.js';
import UmlConnector from '../../Types/UmlConnector.js';
import UmlConnectorPoint from '../../Types/UmlConnectorPoint.js';
import { AnchorCalculator } from '@jxUml';
import ShapeHitTester from './ShapeHitTester.js';
import ConnectorSegmentFinder from './ConnectorSegmentFinder.js';

const { logDebug } = jxLogger('UmlCanvas');

/**
 * Drag-to-create-connector interactions: mousedown on a shape starts a draft
 * line, Q drops a waypoint at the cursor, Escape cancels, mouseup on a target
 * shape commits the connector. Also owns double-click-to-add-waypoint on an
 * existing connector and connector deletion (triggered from UmlConnectorLayer).
 * @param {Function} toWorldPoint - screen -> world coordinate conversion
 * @param {Function} getShapeById - shape lookup by id
 * @returns {{connectorDraft: object|null, draftPoints: Array, handleConnectorDragStart: Function, handleConnectorDoubleClick: Function, handleConnectorPointRemove: Function, handleConnectorDelete: Function}}
 */
export default function useConnectorDrafting(toWorldPoint, getShapeById) {
    const [connectorDraft, setConnectorDraft] = useState(null); // { fromShapeId, x, y, waypoints: [] } in world coords | null

    const handleConnectorDragStart = useCallback(
        function startConnectorDrag(fromShapeId, e) {
            const startWorld = toWorldPoint(e.clientX, e.clientY);
            logDebug('handleConnectorDragStart', `fromShapeId=${fromShapeId} world=${JSON.stringify(startWorld)}`);
            setConnectorDraft({ fromShapeId, ...startWorld, waypoints: [] });
            document.body.style.cursor = 'crosshair';
            document.body.classList.add('uml-connector-creating');

            function onMouseMove(ev) {
                ev.stopImmediatePropagation();
                const worldPoint = toWorldPoint(ev.clientX, ev.clientY);
                setConnectorDraft(function updateDraftPos(prev) {
                    return prev ? { ...prev, ...worldPoint } : prev;
                });
            }

            function cleanup() {
                document.body.style.cursor = '';
                document.body.classList.remove('uml-connector-creating');
                window.removeEventListener('mousemove', onMouseMove, true);
                window.removeEventListener('mouseup', onMouseUp, true);
                window.removeEventListener('keydown', onKeyDown, true);
            }

            // Q drops a waypoint at the current cursor position. Reads prev.x/y via the
            // functional setState form (not a closure variable) to avoid a stale cursor position.
            function onKeyDown(ev) {
                if (ev.key === 'Escape') {
                    setConnectorDraft(null);
                    cleanup();
                    return;
                }
                if (ev.key.toLowerCase() === 'q') {
                    setConnectorDraft(function addWaypoint(prev) {
                        if (!prev) return prev;
                        logDebug('handleConnectorDragStart.onKeyDown', `waypoint added at x=${prev.x} y=${prev.y}`);
                        return { ...prev, waypoints: [...prev.waypoints, { x: prev.x, y: prev.y }] };
                    });
                }
            }

            function onMouseUp(ev) {
                ev.stopImmediatePropagation();
                const worldPoint = toWorldPoint(ev.clientX, ev.clientY);
                const targetShape = ShapeHitTester.findAtPoint(umlCanvasEngine.shapes, worldPoint.x, worldPoint.y, fromShapeId);
                logDebug(
                    'handleConnectorDragStart.onMouseUp',
                    `world=${JSON.stringify(worldPoint)} targetShape=${targetShape?.id ?? 'none'}`,
                );
                if (targetShape) {
                    const connector = new UmlConnector(uuid(), fromShapeId, targetShape.id, 'association');
                    // Reads the latest waypoints via the functional setState form rather than a
                    // separate ref/closure variable, same reasoning as onKeyDown above.
                    setConnectorDraft(function readWaypointsAtDrop(prev) {
                        (prev?.waypoints ?? []).forEach(function addWaypointPoint(wp) {
                            connector.addPoint(new UmlConnectorPoint(uuid(), wp.x, wp.y, connector.id));
                        });
                        return prev;
                    });
                    umlConnectorEngine.addConnector(connector);
                }
                setConnectorDraft(null);
                cleanup();
            }

            window.addEventListener('mousemove', onMouseMove, true);
            window.addEventListener('mouseup', onMouseUp, true);
            window.addEventListener('keydown', onKeyDown, true);
        },
        [toWorldPoint],
    );

    const handleConnectorDoubleClick = useCallback(
        function addWaypointOnConnector(connector, e, points) {
            const worldPoint = toWorldPoint(e.clientX, e.clientY);
            const segmentIndex = ConnectorSegmentFinder.nearestSegmentIndex(points, worldPoint.x, worldPoint.y);
            logDebug(
                'handleConnectorDoubleClick',
                `connectorId=${connector.id} world=${JSON.stringify(worldPoint)} segmentIndex=${segmentIndex}`,
            );
            umlConnectorEngine.insertConnectorPoint(
                connector.id,
                segmentIndex,
                new UmlConnectorPoint(uuid(), worldPoint.x, worldPoint.y, connector.id),
            );
        },
        [toWorldPoint],
    );

    const handleConnectorPointRemove = useCallback(function removeWaypoint(connectorId, pointId) {
        logDebug('handleConnectorPointRemove', `connectorId=${connectorId} pointId=${pointId}`);
        umlConnectorEngine.removeConnectorPoint(connectorId, pointId);
    }, []);

    const handleConnectorDelete = useCallback(function removeConnector(connectorId) {
        logDebug('handleConnectorDelete', `connectorId=${connectorId}`);
        umlConnectorEngine.removeConnector(connectorId);
        // Strips the dead id from viewConfig.userHiddenItems if it was hidden —
        // setItemHidden(id, false) is already a no-op when the id isn't present,
        // so this is safe to call unconditionally. Same "no digitale lijken"
        // reasoning as UmlCanvasEngine.removeShape's removeFromHiddenItems call.
        umlCanvasEngine.setItemHidden(connectorId, false);
    }, []);

    const draftFromShape = connectorDraft ? getShapeById(connectorDraft.fromShapeId) : null;
    const draftFirstTarget = connectorDraft?.waypoints[0] ?? connectorDraft;
    const draftStart = draftFromShape && draftFirstTarget ? AnchorCalculator.edgeToward(draftFromShape, draftFirstTarget.x, draftFirstTarget.y) : null;
    const draftPoints = connectorDraft
        ? [draftStart, ...connectorDraft.waypoints, { x: connectorDraft.x, y: connectorDraft.y }].filter(Boolean)
        : [];

    return {
        connectorDraft,
        draftPoints,
        handleConnectorDragStart,
        handleConnectorDoubleClick,
        handleConnectorPointRemove,
        handleConnectorDelete,
    };
}
